'use client';
import { useState, useEffect } from 'react';
import Header from '@/components/common/Header';
import Footer from '@/components/common/Footer';
import AccessForm, { type GalleryData } from './components/AccessForm';
import PrivateGallery from './components/PrivateGallery';
import HelpSection from './components/HelpSection';

export default function ClientAccessPage() {
  const [authenticated, setAuthenticated] = useState(false);
  const [galleryData, setGalleryData] = useState<GalleryData | null>(null);

  const handleAccessGranted = (data: GalleryData) => {
    setGalleryData(data);
    setAuthenticated(true);
  };

  const handleLogout = () => {
    setGalleryData(null);
    setAuthenticated(false);
  };

  useEffect(() => {
    const reveals = document.querySelectorAll('.reveal');
    if (!reveals.length) return;

    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add('active')),
      { threshold: 0.1 }
    );
    reveals.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, [authenticated]);

  return (
    <>
      <Header />
      <main className="min-h-screen bg-background pt-32 pb-24 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Page Header */}
          <div className="text-center mb-16 reveal">
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-4 tracking-tight">
              Client Gallery Access
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {authenticated
                ? 'Welcome back! Your private photo collection is ready.'
                : 'Welcome back! Access your private photo collection below.'}
            </p>
          </div>

          {/* Content */}
          <div className="reveal delay-200">
            {!authenticated ? (
              <>
                <AccessForm onAccessGranted={handleAccessGranted} />
                <div className="mt-24">
                  <HelpSection />
                </div>
              </>
            ) : (
              galleryData && <PrivateGallery galleryData={galleryData} goBack={handleLogout} />
            )}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}
